/**
 * Hero Featured Report Carousel
 *
 * Simple carousel functionality for the featured report cards
 */

class HeroFeaturedCarousel {
	constructor( element ) {
		this.carousel = element;
		this.cardWrapper = this.carousel.querySelector( '.hero-featured__card-wrapper' );
		this.cards = Array.from( this.carousel.querySelectorAll( '.hero-featured__card' ) );
		this.prevBtn = this.carousel.querySelector( '.hero-featured__nav--prev' );
		this.nextBtn = this.carousel.querySelector( '.hero-featured__nav--next' );
		this.pager = this.carousel.querySelector( '.hero-featured__pager' );

		this.currentIndex = 0;
		this.totalCards = this.cards.length;

		if ( this.totalCards === 0 ) {
			// eslint-disable-next-line no-console
			console.warn( 'Hero Featured Carousel: No cards found' );
			return;
		}

		this.init();
	}

	init() {
		// Set up wrapper for sliding
		this.cardWrapper.style.position = 'relative';
		this.cardWrapper.style.overflow = 'hidden';

		// Create slides container
		this.slidesContainer = document.createElement( 'div' );
		this.slidesContainer.className = 'hero-featured__slides-container';
		this.slidesContainer.style.display = 'flex';
		this.slidesContainer.style.transition = 'transform 0.3s ease-in-out';
		this.slidesContainer.style.width = '100%';

		// Move all cards into slides container
		this.cards.forEach( card => {
			card.style.flex = '0 0 100%';
			card.style.position = 'relative';
			card.style.opacity = '1';
			card.style.visibility = 'visible';
			this.slidesContainer.appendChild( card );
		} );

		this.cardWrapper.insertBefore( this.slidesContainer, this.cardWrapper.firstChild );

		// Set up initial state
		this.updateCarousel( false );

		// Bind event listeners
		if ( this.prevBtn ) {
			this.prevBtn.addEventListener( 'click', () => this.prev() );
		}

		if ( this.nextBtn ) {
			this.nextBtn.addEventListener( 'click', () => this.next() );
		}

		// Hide navigation if only one card
		if ( this.totalCards <= 1 ) {
			if ( this.prevBtn ) this.prevBtn.style.display = 'none';
			if ( this.nextBtn ) this.nextBtn.style.display = 'none';
			if ( this.pager ) this.pager.style.display = 'none';
		}
	}

	prev() {
		this.currentIndex = ( this.currentIndex - 1 + this.totalCards ) % this.totalCards;
		this.updateCarousel( true );
	}

	next() {
		this.currentIndex = ( this.currentIndex + 1 ) % this.totalCards;
		this.updateCarousel( true );
	}

	updateCarousel( animate = true ) {
		// Disable transition if not animating
		if ( !animate ) {
			this.slidesContainer.style.transition = 'none';
		} else {
			this.slidesContainer.style.transition = 'transform 0.3s ease-in-out';
		}

		// Calculate and apply transform
		const translateX = -( this.currentIndex * 100 );
		this.slidesContainer.style.transform = `translateX(${translateX}%)`;

		// Re-enable transition after initial load
		if ( !animate ) {
			// Force reflow
			this.slidesContainer.offsetHeight;
			this.slidesContainer.style.transition = 'transform 0.3s ease-in-out';
		}

		// Update pager if present
		this.updatePager();

		// Update button states
		this.updateButtons();
	}

	updatePager() {
		if ( !this.pager ) return;

		// Create pager dots container if it doesn't exist
		let pagerDots = this.pager.querySelector( '.hero-featured__pager-dots' );

		if ( !pagerDots ) {
			pagerDots = document.createElement( 'div' );
			pagerDots.className = 'hero-featured__pager-dots';
			this.pager.innerHTML = '';
			this.pager.appendChild( pagerDots );

			// Generate dots dynamically based on number of slides
			for ( let i = 0; i < this.totalCards; i++ ) {
				const dot = document.createElement( 'span' );
				dot.className = 'hero-featured__pager-dot';
				dot.setAttribute( 'data-index', i );
				dot.addEventListener( 'click', () => {
					this.currentIndex = i;
					this.updateCarousel( true );
				} );
				pagerDots.appendChild( dot );
			}
		}

		// Update active state for all dots
		const dots = pagerDots.querySelectorAll( '.hero-featured__pager-dot' );
		dots.forEach( ( dot, index ) => {
			if ( index === this.currentIndex ) {
				dot.classList.add( 'hero-featured__pager-dot--active' );
			} else {
				dot.classList.remove( 'hero-featured__pager-dot--active' );
			}
		} );
	}

	updateButtons() {
		// Optional: disable buttons at start/end if not infinite loop
		// For now, we're using infinite loop (wrapping)
	}
}

// Initialize all carousels on page load
function initHeroFeaturedCarousels() {
	const carousels = document.querySelectorAll( '.hero-featured__carousel' );
	carousels.forEach( carousel => {
		new HeroFeaturedCarousel( carousel );
	} );
}

// Initialize when DOM is ready
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initHeroFeaturedCarousels );
} else {
	initHeroFeaturedCarousels();
}

// Export for manual initialization if needed
export default HeroFeaturedCarousel;
